/**
 * Specification List Page
 * @implements FS-004.5.1 - View Specification List
 * @implements US-029 - Basic Specification Viewing
 *
 * Displays all specifications within a project.
 * Route: /projects/:projectId/specifications
 */

import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { SpecificationList } from '@/components/specification';

/**
 * GraphQL query for fetching project title
 */
const GET_PROJECT_QUERY = gql`
  query GetProjectForSpecs($id: ID!) {
    project(id: $id) {
      id
      title
      lifecycleState
    }
  }
`;

/**
 * Project type from the GraphQL response
 */
interface Project {
  id: string;
  title: string;
  lifecycleState: string;
}

/**
 * Response type for GetProject query
 */
interface GetProjectResponse {
  project: Project | null;
}

/**
 * Variables for GetProject query
 */
interface GetProjectVariables {
  id: string;
}

/**
 * SpecificationListPage - Page component for listing specifications
 *
 * @implements FS-004.5.1 - View Specification List
 * @implements US-029 - Basic Specification Viewing
 *
 * Features:
 * - Fetches project data for breadcrumbs
 * - Displays SpecificationList component
 * - Handles loading, error, and 404 states
 * - Provides navigation back to project
 *
 * Route: /projects/:projectId/specifications
 */
export const SpecificationListPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  // Fetch project data for title
  const { data, loading, error } = useQuery<
    GetProjectResponse,
    GetProjectVariables
  >(GET_PROJECT_QUERY, {
    variables: { id: projectId! },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  });

  const project = data?.project;

  // Handle specification click - navigate to detail
  const handleSpecificationClick = (specificationId: string) => {
    navigate(`/projects/${projectId}/specifications/${specificationId}`);
  };

  // Render loading state
  if (loading && !data) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load project: {error.message}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </Button>
        </Box>
      </Container>
    );
  }

  // Render 404 state (project not found)
  if (!project) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Project Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The project you are looking for does not exist or you do not have
              access to it.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/projects')}
            >
              Back to Projects
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/projects"
            color="inherit"
            underline="hover"
          >
            Projects
          </Link>
          <Link
            component={RouterLink}
            to={`/projects/${projectId}`}
            color="inherit"
            underline="hover"
          >
            {project.title}
          </Link>
          <Typography color="text.primary">Specifications</Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Specifications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Functional and business specifications for {project.title}
          </Typography>
        </Box>

        {/* Specification List */}
        <SpecificationList
          projectId={projectId!}
          onSpecificationClick={handleSpecificationClick}
        />

        {/* Back to Project Link */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to={`/projects/${projectId}`}
          >
            Back to Project
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
